import React, { useMemo } from 'react';
import { Chip, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { APP_CONFIG } from '../../../config/app.config';
import { useProductsFilter } from '@features/products/hooks/useProductsFilter';
import { ProductMode } from '@domain/product/models/ProductMode';
import { useCollections } from '@features/collections';
import { useSuppliers } from '@features/suppliers/hooks/useSuppliers';
import { useProductMarketplaces } from '@features/products';
import { ProductFilter } from '@domain/product/models/ProductFilter';
import type { ProductsFilterSectionKey } from './productFilterSectionKeys';

interface ActiveFiltersProps {
  /** When both are set, uses parent-controlled filter instead of the global store */
  filterCriteria?: ProductFilter;
  updateFilter?: (criteria: ProductFilter) => void;
  /** Hide removable chips for these sections (match {@link ProductsFilter} `disabledSections`). */
  disabledSections?: readonly ProductsFilterSectionKey[];
}

export type { ProductsFilterSectionKey } from './productFilterSectionKeys';

const ActiveFilters: React.FC<ActiveFiltersProps> = ({
  filterCriteria: controlledCriteria,
  updateFilter: controlledUpdate,
  disabledSections = [],
}) => {
  const global = useProductsFilter();
  const isControlled = controlledCriteria !== undefined && controlledUpdate !== undefined;
  const filterCriteria = isControlled ? controlledCriteria : global.filterCriteria;
  const updateFilter = isControlled ? controlledUpdate : global.updateFilter;
  const { collections: collectionsResponse } = useCollections();
  const { suppliers } = useSuppliers();
  const { marketplaces } = useProductMarketplaces();
  const collections = collectionsResponse?.status === 'success' ? collectionsResponse.data : [];

  const isSectionHidden = (section: ProductsFilterSectionKey) => disabledSections.includes(section);

  // Generate chips for active filters
  const generateFilterChips = useMemo(() => {
    const chips = [];

    // Collection filter (one chip per selected collection)
    if (!isSectionHidden('collections')) {
      for (const id of filterCriteria?.collectionIds ?? []) {
        const collection = collections.find(c => c.id === id);
        chips.push({
          key: `collection-${id}`,
          label: `Collection: ${collection?.name ?? id}`,
          onDelete: () => {
            const remaining = filterCriteria.collectionIds?.filter(x => x !== id);
            const newCriteria = {
              ...filterCriteria,
              collectionIds: remaining && remaining.length > 0 ? remaining : undefined,
            };
            updateFilter(newCriteria);
          },
        });
      }
    }

    // Supplier filter
    if (!isSectionHidden('supplier') && filterCriteria?.supplierIds && filterCriteria.supplierIds.length > 0) {
      const supplierNames = filterCriteria.supplierIds
        .map(id => suppliers.find(s => s.id === id)?.name ?? '')
        .filter(Boolean);
      chips.push({
        key: 'supplierIds',
        label: `Supplier: ${supplierNames.join(', ')}`,
        onDelete: () => {
          const newCriteria = { ...filterCriteria, supplierIds: undefined };
          updateFilter(newCriteria);
        }
      });
    }

    // Search filter
    if (!isSectionHidden('search') && filterCriteria?.search) {
      chips.push({
        key: 'search',
        label: `Search: "${filterCriteria.search}"`,
        onDelete: () => {
          const newCriteria = { ...filterCriteria, search: undefined };
          updateFilter(newCriteria);
        }
      });
    }

    // Modes filter
    if (!isSectionHidden('modes') && filterCriteria?.modes && filterCriteria.modes.length > 0) {
      const modeLabels = filterCriteria.modes.map(mode => {
        switch (mode) {
          case ProductMode.REVENUE_OPTIMIZED: return 'Revenue optimized';
          case ProductMode.BALANCED: return 'Balanced';
          case ProductMode.COST_OPTIMIZED: return 'Cost optimized';
          default: return mode;
        }
      });
      chips.push({
        key: 'modes',
        label: `Modes: ${modeLabels.join(', ')}`,
        onDelete: () => {
          const newCriteria = { ...filterCriteria, modes: undefined };
          updateFilter(newCriteria);
        }
      });
    }

    // Days of Stock filter
    if (filterCriteria?.daysOfStockMin !== null && filterCriteria?.daysOfStockMin !== undefined) {
      chips.push({
        key: 'daysOfStockMin',
        label: `Min Days: ${filterCriteria.daysOfStockMin}`,
        onDelete: () => {
          const newCriteria = { ...filterCriteria, daysOfStockMin: null };
          updateFilter(newCriteria);
        }
      });
    }
    if (filterCriteria?.daysOfStockMax !== null && filterCriteria?.daysOfStockMax !== undefined) {
      chips.push({
        key: 'daysOfStockMax',
        label: `Max Days: ${filterCriteria.daysOfStockMax}`,
        onDelete: () => {
          const newCriteria = { ...filterCriteria, daysOfStockMax: null };
          updateFilter(newCriteria);
        }
      });
    }

    // Order Amount Days filter
    if (filterCriteria?.orderAmountDaysMin !== null && filterCriteria?.orderAmountDaysMin !== undefined) {
      chips.push({
        key: 'orderAmountDaysMin',
        label: `Min Order Days: ${filterCriteria.orderAmountDaysMin}`,
        onDelete: () => {
          const newCriteria = { ...filterCriteria, orderAmountDaysMin: null };
          updateFilter(newCriteria);
        }
      });
    }
    if (filterCriteria?.orderAmountDaysMax !== null && filterCriteria?.orderAmountDaysMax !== undefined) {
      chips.push({
        key: 'orderAmountDaysMax',
        label: `Max Order Days: ${filterCriteria.orderAmountDaysMax}`,
        onDelete: () => {
          const newCriteria = { ...filterCriteria, orderAmountDaysMax: null };
          updateFilter(newCriteria);
        }
      });
    }

    // Lead Time filter
    if (filterCriteria?.leadTimeMin !== null && filterCriteria?.leadTimeMin !== undefined) {
      chips.push({
        key: 'leadTimeMin',
        label: `Min Lead Time: ${filterCriteria.leadTimeMin} days`,
        onDelete: () => {
          const newCriteria = { ...filterCriteria, leadTimeMin: null };
          updateFilter(newCriteria);
        }
      });
    }
    if (filterCriteria?.leadTimeMax !== null && filterCriteria?.leadTimeMax !== undefined) {
      chips.push({
        key: 'leadTimeMax',
        label: `Max Lead Time: ${filterCriteria.leadTimeMax} days`,
        onDelete: () => {
          const newCriteria = { ...filterCriteria, leadTimeMax: null };
          updateFilter(newCriteria);
        }
      });
    }

    // Has Image filter
    if (filterCriteria?.hasImage === true) {
      chips.push({
        key: 'hasImage',
        label: 'Has Image',
        onDelete: () => {
          const newCriteria = { ...filterCriteria, hasImage: undefined };
          updateFilter(newCriteria);
        }
      });
    }

    // Forecast Grades filter
    if (!isSectionHidden('forecastGrades') && filterCriteria?.forecastGrades && filterCriteria.forecastGrades.length > 0) {
      const gradeLabels = filterCriteria.forecastGrades.map(g => (g === 'NONE' ? 'None' : g));
      chips.push({
        key: 'forecastGrades',
        label: `Forecast Grades: ${gradeLabels.join(', ')}`,
        onDelete: () => {
          const newCriteria = { ...filterCriteria, forecastGrades: undefined };
          updateFilter(newCriteria);
        }
      });
    }

    if (!isSectionHidden('marketplaces') && filterCriteria?.noMarketplaceListing) {
      chips.push({
        key: 'noMarketplaceListing',
        label: 'Unavailable on marketplaces',
        onDelete: () => {
          const newCriteria = { ...filterCriteria, noMarketplaceListing: undefined };
          updateFilter(newCriteria);
        }
      });
    }

    if (!isSectionHidden('marketplaces') && filterCriteria?.marketplaceIds && filterCriteria.marketplaceIds.length > 0) {
      const names = filterCriteria.marketplaceIds
        .map(id => marketplaces.find(m => m.id === id)?.name ?? id)
        .filter(Boolean);
      chips.push({
        key: 'marketplaceIds',
        label: `Marketplaces: ${names.join(', ')}`,
        onDelete: () => {
          const newCriteria = { ...filterCriteria, marketplaceIds: undefined };
          updateFilter(newCriteria);
        }
      });
    }

    if (filterCriteria?.purchasable === false) {
      chips.push({
        key: 'purchasable',
        label: 'Including not available for purchase',
        onDelete: () => {
          const newCriteria = { ...filterCriteria, purchasable: undefined };
          updateFilter(newCriteria);
        }
      });
    }

    return chips;
  }, [filterCriteria, updateFilter, collections, suppliers, marketplaces, disabledSections]);

  const activeChips = generateFilterChips;

  if (activeChips.length === 0) {
    return null;
  }

  return (
    <Stack direction="row" gap={1} alignItems="center" overflow="auto" py={1}>
      {activeChips.map((chip) => (
        <Chip
          key={chip.key}
          label={chip.label}
          onDelete={chip.onDelete}
          deleteIcon={<CloseIcon />}
          size="small"
          sx={{
            borderColor: APP_CONFIG.ui.theme.colors.border,
            color: APP_CONFIG.ui.theme.colors.primaryText,
            '& .MuiChip-deleteIcon': {
              color: APP_CONFIG.ui.theme.colors.primaryText,
              '&:hover': {
                color: APP_CONFIG.ui.theme.colors.error
              }
            }
          }}
        />
      ))}
    </Stack>
  );
};

export default ActiveFilters;
